
/**
 * Data Table to wrap data elements.
 * @class DataTableElement
 */
class DataTableElement extends DataTableCommon {

  /**
   * Template for DataTableElement.
   * @param {object} args Template arguments.
   * @param {string[]} args.columns List of column ids.
   * @param {string[]} args.sortable List of column ids that should be sortable.
   * @param {string[]} args.sticky List of column ids that should be sticky.
   * @param {string|null} args.sortBy Current column id that the table is sorted by. Null if no sorting is applied.
   * @param {string} args.sortDirection Current sort direction ('asc' or 'desc').
   * @param {number} args.numOfRows Number of rows in the table, used for aria-rowcount attribute.
   * @param {number} args.maxVisibleRows Maximum number of visible rows in the table.
   */
  static template(args = {}) {

    // default values for template arguments
    args = {
      columns: [],
      sortable: [],
      sticky: [],
      sortBy: null,
      sortDirection: 'asc',
      numOfRows: 0,
      maxVisibleRows: Infinity,
      ...args
    };

    return /*html*/ `
      <template>
        <style>
          ${super.styles}

          :host {
            position: relative;
            display: block;
            font-size: var(--body-font-size-s);
            overflow-x: auto;
          }

          .table {
            display: table;
            width: 100%;
            height: 100%; /* CSS Bugfix for Safari */
          }

          .table__header {
            display: table-header-group;
            background-color: var(--color-contrast-1);
          }

          :host([variant="flat"]) .table__header {
            background-color: transparent;
          }

          .table__body {
            display: table-row-group;
          }

          .table__cell .inner {
            background-color: var(--color-contrast-1);
          }

          :host([variant="flat"]) .table__cell .inner {
            background-color: transparent;
          }

          .table__cell--head {
            font-weight: bold;
            white-space: nowrap;
          }

          .table__sort-icon {
            appearance: none;
            border: none;
            background: none;
            padding: 0;            
          }
          .table__sort-icon:focus-visible {
            outline: var(--space-1, 2px) solid var(--color-accent);
            outline-offset: var(--space-2, 2px);
          }

          .table__sort-icon::after {
            content: '';
            display: inline-block;
            width: 16px;
            height: 20px;
            margin-top: -5px;
            margin-bottom: -5px;
            background-color: var(--color-contrast-4);
            cursor: pointer;
            -webkit-mask-image: url(${window.confEnv?.www_url || "//erco.com"}/struktur/builds/sprite-basic.svg);
            mask-image: url(${window.confEnv?.www_url || "//erco.com"}/struktur/builds/sprite-basic.svg);
            -webkit-mask-position: -1170px -30px;
            mask-position: -1170px -30px;
            transform: rotate(0deg);
            transition:
              background-color var(--duration-moderate-02),
              transform var(--duration-moderate-02);
          }
          .table__cell--head:hover .table__sort-icon::after,
          .table__sort-icon:hover::after,
          .table__sort-icon:focus::after {
            background-color: var(--link-color-text-hover);
          }
          .table__sort-icon:active::after {
            background-color: var(--link-color-text-active);
          }
          
          .table__sort-icon[data-current="true"]::after {
            background-color: var(--color-contrast-7);
          }

          :host([sort-direction="desc"]) .table__sort-icon[data-current="true"]::after {
            transform: rotate(180deg);
          }

          ${args.maxVisibleRows !== Infinity ? /* css */`
          :host([collapse-enabled]) ::slotted(data-table-item:nth-of-type(n + ${args.maxVisibleRows + 1})) { /* Also include header */
            display: none;
          }
          ` : ''}
        </style>
        <div class="table" role="table" aria-rowcount="${args.numOfRows}">
          <div class="table__header" role="rowgroup">
            <div class="table__row" part="row" role="row">
              ${args.columns?.map(id => /* html */`
                <div
                  role="columnheader"
                  ${args.sortable?.includes(id) ? /* html */`
                    aria-sort="${args.sortBy === id ? (args.sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}"
                  ` : ''}
                  part="col-${id} col"
                  data-col="${id}"
                  class="table__cell table__cell--head ${args.sticky?.includes(id) ? 'table__cell--sticky' : ''}"
                  ${args.sticky?.includes(id) ? `
                    style="--sticky-cell-offset-left: var(--sticky-cell-offset-left-${id},0); --sticky-cell-offset-right: var(--sticky-cell-offset-right-${id},0)"
                    data-sticky-active="false"
                  ` : ''}
                >
                  <div class="inner">
                    <slot name="${id}"></slot>
                     ${args.sortable?.includes(id) ? /* html */`
                      <button class="table__sort-icon" data-js-sort-icon="${id}" data-current="${args.sortBy === id}"></button>
                    ` : ''}
                  </div>
                </div>
              `).join('') ?? ''}
            </div>
          </div>
          <div class="table__body" role="rowgroup">
            <slot></slot>
          </div>
        </div>
      </template>
    `;
  }

  /** @type Object.<string,number> */
  stickyCellWidth = {};

  /** @type Object.<string,{left: number, right: number}> */
  stickyCellOffset = {};

  /** @type ResizeObserver */
  resizeObserver;

  /** @type boolean */
  _isSorting = false;

  /** @type Intl.Collator */
  _collator = new Intl.Collator(document.documentElement.lang?.trim() || undefined, { numeric: true, sensitivity: 'base' });

  /** @type Function | undefined */
  onSortChange = undefined;

  /** @type Array<() => Promise<void>> */
  _beforeSortCallbacks = [];

  /** @type boolean */
  _suppressSortHooks = true;

  /** @type number | undefined */
  _sortChangeDispatchTimeout;

  constructor() {
    super();

    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const cellElement = entry.target;
        const slotElement = cellElement.querySelector('slot');

        this.stickyCellWidth[slotElement.getAttribute('name')] = cellElement.offsetWidth;
      }
      this._updateStickyCellOffset();
    });
  }

  connectedCallback() {
    if (super.connectedCallback) {
      super.connectedCallback();
    }

    // Defer enabling sort hooks until after initial render to prevent unwanted sort calls during setup
    queueMicrotask(() => {
      this._suppressSortHooks = false;
    });
  }

  render() {
    const numOfRows = this.querySelectorAll('data-table-item').length;

    this.shadowRoot.innerHTML = this.constructor.template({
      columns: this.columns,
      sortable: this.sortable,
      sticky: this.sticky,
      sortBy: this.sortBy,
      sortDirection: this.sortDirection,
      numOfRows: numOfRows,
      maxVisibleRows: this.maxVisibleRows
    });

    const template = this.shadowRoot.querySelector('template');
    const clone = template.content.cloneNode(true);
    this.shadowRoot.appendChild(clone);

    this.resizeObserver.disconnect();
    this.stickyCellWidth =
      Object.fromEntries(this.sticky.map(column => ([column, 0])));

    this.$headerCells = this.shadowRoot.querySelectorAll('.table__cell--head');
    this.$headerCells.forEach($cell => {
      const $slot = $cell.querySelector('slot');
      const column = $slot.getAttribute('name');

      // add sticky cells to array
      if (this.sticky.includes(column)) {
        this.resizeObserver.observe($cell);
        this.stickyCellWidth[column] = $cell.offsetWidth;
        this._updateStickyCellOffset();
      }

      // check if column is sortable
      if (!this.sortable.includes(column)) {
        return;
      }

      $cell.addEventListener('click', () => {
        const newSortBy = column;
        const newSortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';

        this.sortDirection = (this.sortBy !== newSortBy) ? 'asc' : newSortDirection;
        this.sortBy = newSortBy;

        this.dispatchEvent(new CustomEvent('sort-click', {
          detail: {
            sortBy: this.sortBy,
            sortDirection: this.sortDirection
          }
        }));
      });
    });

    this.$mainSlot = this.shadowRoot.querySelector('slot:not([name])');

    this.$mainSlot.addEventListener('slotchange', this._onSlotChange.bind(this));

    // Update sort icon and aria-sort after render to reflect current state
    if (this.sortBy) {
      this._changeSortIcon();
    }
  }

  static get observedAttributes() {
    return [
      'sort-by',
      'sort-direction',
      'columns',
      'sortable',
      'sticky',
      'variant',
      'collapse-enabled',
      'max-visible-rows'
    ];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      this[name] = newValue;
    }
  }

  // sort-by

  get sortBy() {
    return this.getAttribute('sort-by');
  }

  set "sort-by"(value) {
    this.sortBy = value;
  }
  set sortBy(value) {
    this._setAttributeIfNotAlreadySet('sort-by', value);
    this.sort();
    this._changeSortIcon();

    this._dispatchSortChangeEvent();
  }

  // sort-direction

  get sortDirection() {
    return this.getAttribute('sort-direction');
  }

  set "sort-direction"(value) {
    this.sortDirection = value;
  }
  set sortDirection(value) {
    this._setAttributeIfNotAlreadySet('sort-direction', value);
    this.sort();
    this._changeSortIcon();

    this._dispatchSortChangeEvent();
  }

  // columns

  get columns() {
    return (this.getAttribute('columns') ?? '').split(',');
  }

  set columns(value) {
    if (Array.isArray(value)) {
      value = value.join(',');
    }

    this._setAttributeIfNotAlreadySet('columns', value);
    this.render();

    // rerender all rows
    const rows = Array.from(this.shadowRoot.querySelector('slot:not([name])').assignedElements());
    rows.forEach(rowElement => {
      if (!rowElement.render) {
        return;
      }

      rowElement.render();
    });
  }

  // sortable

  get sortable() {
    return (this.getAttribute('sortable') ?? '').split(',');
  }

  set sortable(value) {
    if (Array.isArray(value)) {
      value = value.join(',');
    }

    this._setAttributeIfNotAlreadySet('sortable', value);
    this.render();
  }

  // sticky

  get sticky() {
    return (this.getAttribute('sticky') ?? '').split(',');
  }

  set sticky(value) {
    if (Array.isArray(value)) {
      value = value.join(',');
    }

    this._setAttributeIfNotAlreadySet('sticky', value);

    this.render();
  }

  // variant

  get variant() {
    return this.getAttribute('variant');
  }

  set variant(value) {
    this._setAttributeIfNotAlreadySet('variant', value);
  }

  // collapse-enabled

  get collapseEnabled() {
    return this.hasAttribute('collapse-enabled')
      && this.getAttribute('collapse-enabled') !== 'false';
  }

  set "collapse-enabled"(value) {
    this.collapseEnabled = value;
  }
  set collapseEnabled(value) {
    if (value === '' || !!value) {
      this._setAttributeIfNotAlreadySet('collapse-enabled', 'true');
    } else {
      this.removeAttribute('collapse-enabled');
    }
  }

  // max-visible-rows

  get maxVisibleRows() {
    return parseInt(this.getAttribute('max-visible-rows')) || 0;
  }

  set "max-visible-rows"(value) {
    this.maxVisibleRows = value;
  }
  set maxVisibleRows(value) {
    this._setAttributeIfNotAlreadySet('max-visible-rows', value);
    this.render();
  }

  //
  // Methods

  /**
   * Sorts the table rows based on the current sortBy and sortDirection values.
   * 
   * waits for all beforeSortCallbacks to resolve before performing the sort.
   * Uses a timeout to batch multiple rapid sort changes and minimize DOM updates.
   */
  sort() {
    if (this.sortTimeout) {
      clearTimeout(this.sortTimeout);
    }

    const suppressSortHooks = this._suppressSortHooks;

    this.sortTimeout = setTimeout(() => {
      const beforeSortPromises = !suppressSortHooks
        ? this._beforeSortCallbacks.map(callback => Promise.resolve().then(() => callback()))
        : [];

      Promise.allSettled(beforeSortPromises)
        .then(() => {
          const sortBy = this.sortBy ?? null;
          const sortDirection = this.sortDirection ?? 'asc';

          if (!sortBy) {
            return;
          }

          const rows = Array.from(this.shadowRoot.querySelector('slot:not([name])').assignedElements());

          // Cache Values before sorting to minimize DOM access during sort
          const rowsWithValues = rows.map(row => {
            const element = row.querySelector(`[slot="${sortBy}"]`);
            const value = element?.textContent?.trim() ?? "";
            return { row, value };
          });

          // Sort with pre-computed direction multiplier and cached Intl.Collator
          const directionMultiplier = sortDirection === 'asc' ? 1 : -1;
          rowsWithValues.sort((a, b) => {
            return this._collator.compare(a.value, b.value) * directionMultiplier;
          });

          // Extract sorted rows
          const sortedRows = rowsWithValues.map(item => item.row);

          // Start sorting process flag
          this._isSorting = true;

          // Check if already sorted to avoid unnecessary DOM manipulation
          const currentOrder = rows;
          const needsUpdate = !sortedRows.every((row, i) => row === currentOrder[i]);

          if (needsUpdate) {
            // Use DocumentFragment for batch DOM updates (single reflow instead of multiple)
            const fragment = document.createDocumentFragment();
            sortedRows.forEach(row => fragment.appendChild(row));
            this.appendChild(fragment);
          }

          // Reset flag after DOM has settled
          requestAnimationFrame(() => {
            this._isSorting = false;
          });
        });
    }, 5);
  }

  /**
   * Adds a callback to be executed before sorting. This can be used to perform asynchronous operations (e.g. data fetching) before the actual sorting is done.
   * @param {() => Promise<any>} callback 
   */
  addBeforeSortCallback(callback) {
    if (typeof callback === 'function') {
      this._beforeSortCallbacks.push(callback);
    }
  }

  /**
   * Shows the sort icon of the current sort column and updates aria-sort attributes.
   * @protected
   */
  _changeSortIcon() {
    const currentSortIconElement = this.shadowRoot.querySelector(`[data-js-sort-icon="${this.sortBy}"]`);
    const sortIconElements = this.shadowRoot.querySelectorAll('[data-js-sort-icon]');

    // hide all sort icons and reset aria-sort
    sortIconElements.forEach(iconElement => {
      iconElement.dataset.current = false;

      // Update aria-sort on the parent cell
      const headerCell = iconElement.closest('[role="columnheader"]');
      if (headerCell) {
        headerCell.setAttribute('aria-sort', 'none');
      }
    });

    if (!currentSortIconElement) {
      return;
    }

    // show sort icon of current sort column and set aria-sort
    currentSortIconElement.dataset.current = true;

    const currentHeaderCell = currentSortIconElement.closest('[role="columnheader"]');
    if (currentHeaderCell) {
      const sortValue = this.sortDirection === 'asc' ? 'ascending' : 'descending';
      currentHeaderCell.setAttribute('aria-sort', sortValue);
    }
  }

  /**
   * Updates the offset of the sticky cells depending on the width of other sticky cells.
   * @protected
   */
  _updateStickyCellOffset() {
    this.sticky.forEach(column => {
      let offsetLeft = 0;
      let offsetRight = 0;

      let colIndex = this.columns.indexOf(column);

      this.columns.forEach((col, index) => {
        if (index < colIndex) {
          offsetLeft += this.stickyCellWidth[col] || 0;
        } else if (index > colIndex) {
          offsetRight += this.stickyCellWidth[col] || 0;
        }
      });

      this.shadowRoot.host.style.setProperty(`--sticky-cell-offset-left-${column}`, `${offsetLeft}px`);
      this.shadowRoot.host.style.setProperty(`--sticky-cell-offset-right-${column}`, `${offsetRight}px`);

      this.stickyCellOffset[column] = {
        left: offsetLeft,
        right: offsetRight
      };
    });
  }

  _onSlotChange() {
    const customEvent = new CustomEvent('main-slot-change');
    this.dispatchEvent(customEvent);

    this.shadowRoot.querySelector('[role="table"]')
      .setAttribute('aria-rowcount',
        this.querySelectorAll('data-table-item').length
      );

    // Redo sorting only if not currently sorting (prevents infinite loop)
    // and only if new items were added/removed
    if (!this._isSorting) {
      this.sort();
    }
  }

  _dispatchSortChangeEvent() {
    if (this._suppressSortHooks) {
      return;
    }

    if (this._sortChangeDispatchTimeout) {
      clearTimeout(this._sortChangeDispatchTimeout);
    }

    // Defer dispatch so rapid updates to sortBy/sortDirection emit one consolidated change.
    this._sortChangeDispatchTimeout = setTimeout(() => {
      this._sortChangeDispatchTimeout = undefined;

      const eventDetail = {
        sortBy: this.sortBy,
        sortDirection: this.sortDirection
      };

      // Call onSortChange callback if defined
      if (typeof this.onSortChange === 'function') {
        this.onSortChange(eventDetail);
      }

      // Dispatch sort-change event for external listeners
      this.dispatchEvent(new CustomEvent('sort-change', {
        detail: eventDetail
      }));
    }, 5);
  }

}

window.DataTableElement = DataTableElement;
customElements.define('data-table', DataTableElement);